"use client";

import OnlineMasterClassCard from "@/components/molecules/OnlineMasterClassCard";
import Carousel1 from "@/components/organisms/carousels/Carousel1";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  carouselClick,
  leadCtaClick,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const OnlineMasterclass1 = ({ data }: { data: any }) => {
  const { data: sectionData } = data;
  const ref = useRef<HTMLElement>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const previousIndexRef = useRef<number | null>(null);

  // Extract data early, before early returns
  const {
    metadata = [],
    layout_variant = "variant_1",
    title = "",
    title_text_color = "#FFFFFF",
    background_image = "/images/master-class.webp",
    left_navigation_icon = "",
    left_navigation_icon_alt = "",
    right_navigation_icon = "",
    right_navigation_icon_alt = "",
    button_text = "",
    button_background_color = "",
    button_text_color = "",
    button_icon = "",
    button_hover_icon = "",
    show_button = true,
    show_quote_icon = true,
    quote_icon = "",
    quote_icon_alt = "",
  } = sectionData || {};

  const isV2 = layout_variant === "variant_2";

  // Sort metadata by priority - MUST be called before early returns
  const sortedMetadata = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .sort((a: any, b: any) => a.meta_priority - b.meta_priority)
      .filter((item: any) => item.meta_status);
  }, [metadata]);

  // All hooks MUST be called before early returns
  const isMobile = useIsMobile(800);

  // Helper function to get the appropriate image based on device type
  const getImageForDevice = useCallback(
    (item: any, isMobileDevice: boolean | null) => {
      if (!item) return "";
      // If isMobile is null (initial state), check window width directly
      if (isMobileDevice === null && typeof window !== "undefined") {
        isMobileDevice = window.innerWidth < 800;
      }
      return isMobileDevice
        ? item.meta_mobile_image
        : item.meta_desktop_image || "";
    },
    [],
  );

  // Track selected index instead of item object for reliable change detection
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  // Derive selected item from index
  const selectedItem = useMemo(() => {
    return sortedMetadata.length > 0 &&
      selectedIndex >= 0 &&
      selectedIndex < sortedMetadata.length
      ? sortedMetadata[selectedIndex]
      : null;
  }, [sortedMetadata, selectedIndex]);

  // Every background layer carries BOTH device assets and lets CSS pick between
  // them. `isMobile` is null on the first client render (it has to be, to match
  // the server HTML), so a JS-picked URL would paint the desktop asset on a
  // phone — and since these layers are `bg-cover`, a landscape asset in a tall
  // narrow box blows up to a cropped close-up. It also never self-corrects: the
  // effect below only re-renders when the resolved URL changes, so slide 0 kept
  // the server-rendered desktop URL until the user navigated away and back.
  const imagePairByUrl = useMemo(() => {
    const map = new Map<string, { mobile: string; desktop: string }>();
    sortedMetadata.forEach((item: any) => {
      const pair = {
        mobile: item?.meta_mobile_image || item?.meta_desktop_image || "",
        desktop: item?.meta_desktop_image || item?.meta_mobile_image || "",
      };
      if (pair.mobile) map.set(pair.mobile, pair);
      if (pair.desktop) map.set(pair.desktop, pair);
    });
    return map;
  }, [sortedMetadata]);

  const backgroundVars = useCallback(
    (url: string): CSSProperties => {
      const pair = imagePairByUrl.get(url) ?? { mobile: url, desktop: url };
      return {
        "--mc-bg-mobile": pair.mobile ? `url("${pair.mobile}")` : "none",
        "--mc-bg-desktop": pair.desktop ? `url("${pair.desktop}")` : "none",
      } as CSSProperties;
    },
    [imagePairByUrl],
  );

  // Deterministic across server + first client render; the crossfade state below
  // only decides WHICH slide is showing, never which device asset.
  const getInitialImage = () => {
    if (sortedMetadata.length === 0) return "";
    return (
      sortedMetadata[0]?.meta_desktop_image ||
      sortedMetadata[0]?.meta_mobile_image ||
      ""
    );
  };

  const initialImage = getInitialImage();
  const [currentImage, setCurrentImage] = useState<string>(initialImage);
  const [nextImage, setNextImage] = useState<string>(initialImage);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const imageLoadRef = useRef<Map<string, Promise<void>>>(new Map());
  const targetImageRef = useRef<string>("");
  const currentImageRef = useRef<string>(initialImage);

  // Early returns AFTER all hooks are called
  if (!sectionData) return null;

  // Preload image function
  const preloadImage = (src: string): Promise<void> => {
    if (!src) return Promise.resolve();

    if (imageLoadRef.current.has(src)) {
      return imageLoadRef.current.get(src)!;
    }

    const promise = new Promise<void>((resolve) => {
      const img = document.createElement("img");
      img.onload = () => resolve();
      img.onerror = () => resolve(); // Resolve even on error to not block transition
      img.src = src;
    });

    imageLoadRef.current.set(src, promise);
    return promise;
  };

  // Update ref whenever currentImage changes
  useEffect(() => {
    currentImageRef.current = currentImage;
  }, [currentImage]);

  useEffect(() => {
    // Skip if isMobile is still null (initial state)
    if (isMobile === null) return;

    // Get the current item based on selectedIndex
    const currentItem =
      sortedMetadata.length > 0 &&
      selectedIndex >= 0 &&
      selectedIndex < sortedMetadata.length
        ? sortedMetadata[selectedIndex]
        : null;

    if (!currentItem) return;

    const newImage = getImageForDevice(currentItem, isMobile);

    // If no new image, don't proceed
    if (!newImage) return;

    // Always clear any existing timer/transition when selectedIndex changes
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Set target image immediately with the selectedIndex as identifier
    targetImageRef.current = newImage;

    // If the new image is the same as current, update refs but skip transition
    if (newImage === currentImageRef.current) {
      // Still update the state to ensure consistency
      setCurrentImage(newImage);
      setNextImage(newImage);
      setIsTransitioning(false);
      return;
    }

    // Reset transition state
    setIsTransitioning(false);

    // Start transition
    setIsTransitioning(true);

    // Capture the selectedIndex at the start of this transition
    const transitionStartIndex = selectedIndex;

    // Preload the new image before starting transition
    preloadImage(newImage).then(() => {
      // Check if selectedIndex has changed during preload
      if (selectedIndex !== transitionStartIndex) {
        setIsTransitioning(false);
        return;
      }

      // Verify the image is still correct
      const verifyItem =
        sortedMetadata.length > 0 &&
        selectedIndex >= 0 &&
        selectedIndex < sortedMetadata.length
          ? sortedMetadata[selectedIndex]
          : null;
      const verifyImage = verifyItem
        ? getImageForDevice(verifyItem, isMobile)
        : "";

      if (verifyImage !== newImage || targetImageRef.current !== newImage) {
        setIsTransitioning(false);
        return;
      }

      // Set next image after it's loaded
      setNextImage(newImage);

      // Use requestAnimationFrame to ensure the DOM has updated before starting transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Check again before starting timeout
          if (selectedIndex !== transitionStartIndex) {
            setIsTransitioning(false);
            return;
          }

          // After transition completes, update current image
          timerRef.current = setTimeout(() => {
            // Final check - only update if we're still on the same index
            if (selectedIndex === transitionStartIndex) {
              const finalItem =
                sortedMetadata.length > 0 &&
                selectedIndex >= 0 &&
                selectedIndex < sortedMetadata.length
                  ? sortedMetadata[selectedIndex]
                  : null;
              const finalImage = finalItem
                ? getImageForDevice(finalItem, isMobile)
                : "";

              if (finalImage === newImage) {
                setCurrentImage(newImage);
                setNextImage(newImage);
                setIsTransitioning(false);
                if (targetImageRef.current === newImage) {
                  targetImageRef.current = "";
                }
              } else {
                setIsTransitioning(false);
              }
            } else {
              setIsTransitioning(false);
            }
            timerRef.current = null;
          }, 500); // Match transition duration
        });
      });
    });

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [selectedIndex, isMobile, sortedMetadata, getImageForDevice]);

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "online_masterclass_section");
  //   }
  // }, []);

  // Track carousel slide changes - memoized to prevent multiple event listeners
  const handleSlideChange = useCallback(
    (index: number) => {
      // Update selected index for image transition
      setSelectedIndex(index);

      // Only track if index actually changed
      if (previousIndexRef.current !== index && sortedMetadata[index]) {
        const currentItem = sortedMetadata[index];
        let label =
          currentItem.meta_name || title || `masterclass_slide_${index + 1}`;

        // Add direction label if not initial load
        if (previousIndexRef.current !== null) {
          const previousIndex = previousIndexRef.current;
          label = index > previousIndex ? "right" : "left";
        }

        previousIndexRef.current = index;
        setCurrentSlideIndex(index);
        carouselClick({
          section: "online_masterclass",
          label: label,
        });
      } else {
        setCurrentSlideIndex(index);
      }
    },
    [sortedMetadata, title],
  );

  // Only animate opacity while two different image URLs are active. When the transition
  // ends we set current === next; if we keep duration-500 then, the browser animates
  // (0,100) → (100,0) on the same two layers and causes a second “flicker” even though
  // the image is unchanged. Crossfades can also show the section background — use bg-black
  // underneath.
  const isBackgroundCrossfading = currentImage !== nextImage;
  const bgTransitionClass = isBackgroundCrossfading
    ? "transition-opacity duration-500 ease-in-out will-change-opacity"
    : "transition-none";

  // Handle CTA button click
  const handleCtaClick = (index: number) => {
    const currentItem = sortedMetadata[index];
    leadCtaClick({
      section: "online_masterclass",
      component: "masterclass_cta",
      label: currentItem?.meta_name || "enroll_now",
      index: index,
      parentEntity: `masterclass_slide_${index + 1}`,
    });
  };

  return (
    <section
      ref={ref}
      className={cn(
        "relative isolate flex flex-col items-center justify-center text-white",
        "bg-black",
        "xs:px-5 xs:py-5 xs:gap-6",
        "sm:gap-[66px] sm:pb-0 sm:pt-[18px] w-full overflow-hidden",
      )}
    >
      {/* Background image layers for smooth transition */}
      <div
        className={cn(
          "absolute inset-0 bg-cover bg-center",
          "[background-image:var(--mc-bg-mobile)] min-[800px]:[background-image:var(--mc-bg-desktop)]",
          bgTransitionClass,
          isBackgroundCrossfading ? "opacity-0" : "opacity-100",
        )}
        style={{
          ...backgroundVars(currentImage),
          backgroundColor: currentImage ? "transparent" : "#000",
          zIndex: 0,
        }}
        aria-hidden
      />
      <div
        className={cn(
          "absolute inset-0 bg-cover bg-center",
          "[background-image:var(--mc-bg-mobile)] min-[800px]:[background-image:var(--mc-bg-desktop)]",
          bgTransitionClass,
          isBackgroundCrossfading ? "opacity-100" : "opacity-0",
        )}
        style={{
          ...backgroundVars(nextImage),
          backgroundColor: nextImage ? "transparent" : "#000",
          zIndex: 1,
        }}
        aria-hidden
      />
      {/* Content */}
      <div className="relative z-10 w-full">
        <div className={cn("flex items-center justify-center", "sm:px-4")}>
          <h2
            className={cn(
              "text-center overflow-hidden leading-normal",
              "sm:mb-[50px] max-[799px]:mb-6",
              isV2
                ? cn(
                    "font-poppins font-semibold tracking-[-0.54px]",
                    "text-[28px] md:text-[32px] lg:text-[36px] xl:text-[42px]",
                    // Figma: heading stays on a single line on web (whitespace-nowrap);
                    // only wraps on mobile, matching the mobile design.
                    "wrap-break-word sm:whitespace-nowrap",
                  )
                : cn(
                    "line-clamp-2 wrap-break-word",
                    "sm:max-w-[572px] lg:max-w-[572px] xl:max-w-[912px]",
                    "tracking-0 font-wf-visual-sans type-h3",
                    "text-heading-6-semibold",
                    "sm:text-heading-5-semibold",
                    "md:text-heading-4-semibold",
                    "lg:text-new-heading-34-semibold",
                    "xl:text-new-heading-42-semibold",
                  ),
            )}
            style={{ color: title_text_color }}
          >
            {title}
          </h2>
        </div>
        <Carousel1
          leftNavigationIcon={
            <Image
              src={left_navigation_icon || ""}
              alt={left_navigation_icon_alt}
              className="w-[36px] h-[36px] sm:w-[38px] sm:h-[38px] md:w-[42px] md:h-[42px] lg:w-[56px] lg:h-[56px] xl:w-[52px] xl:h-[52px] object-contain "
              width={52}
              height={52}
            />
          }
          rightNavigationIcon={
            <Image
              src={right_navigation_icon || ""}
              alt={right_navigation_icon_alt}
              width={52}
              height={52}
              className="w-[36px] h-[36px] sm:w-[38px] sm:h-[38px] md:w-[42px] md:h-[42px] lg:w-[56px] lg:h-[56px] xl:w-[52px] xl:h-[52px] object-contain"
            />
          }
          options={{ loop: false, duration: 45 }}
          navigationButtonClassName={cn(
            "absolute",
            "xs:w-[320px] xs:top-[40%] xs:-translate-y-[40%]",
            "sm:w-[932px] sm:top-[14%] sm:-translate-y-1/3",
            "md:w-[932px] md:top-[16%] md:-translate-y-1/3",
            "lg:w-[1194px] lg:top-[14%] lg:-translate-y-1/3",
            "xl:w-[1754px] xl:top-[22%] xl:-translate-y-1/2 xl:px-10",
            {
              hidden: sortedMetadata.length <= 1,
            },
          )}
          navigationButtonsType={2}
          autoplay={false}
          onSlideChange={handleSlideChange}
        >
          {sortedMetadata.map((item: any, index: number) => (
            <div className="" key={index}>
              <OnlineMasterClassCard
                item={item}
                variant={layout_variant}
                buttonText={button_text}
                buttonBackgroundColor={button_background_color}
                buttonTextColor={button_text_color}
                buttonIcon={button_icon}
                onButtonClick={() => handleCtaClick(index)}
                buttonHoverIcon={button_hover_icon}
                showButton={show_button}
                showQuoteIcon={show_quote_icon}
                quoteIcon={quote_icon}
                quoteIconAlt={quote_icon_alt}
              />
            </div>
          ))}
        </Carousel1>
      </div>
    </section>
  );
};

export default OnlineMasterclass1;
